/* @flow */
import _ from 'lodash';

import Store from 'lib/Store';
import Messages from 'collections/Messages';
import Message from 'entities/Message';
import { getGroupKey } from 'utils/roleHelpers';

import { ActionTypes, LoadingStates } from '../constants/GroupConstants';

const getGroupKeys = (message: Message): Array<string | number> => {
  const threadKeys = message.getThreads().map(t => getGroupKey(t.group_id, t.role));
  // XXX rgoldfinger why do messages in tests only have targets?
  const targetKeys = message.getTargets().map(t => getGroupKey(t.group_id, t.role));
  return _.uniq([...threadKeys, ...targetKeys]);
};

class MessageStore extends Store {
  getInitialState() {
    return {
      messages: {},
      messagesScheduled: {},
      loadingState: {},
      gaps: {},
    };
  }

  doMessage(dict, method, message) {
    const groupKeys = getGroupKeys(message);
    let changed = false;

    groupKeys.forEach(groupKey => {
      let messages = dict[groupKey];
      if (!messages) {
        dict[groupKey] = new Messages();
        messages = dict[groupKey];
      }
      messages[method](message);
      changed = true;
    });

    if (changed) {
      this.emitChange();
    }
  }

  addMessage(message) {
    // This is when a message was scheduled, then an edit
    // was made that unscheduled it, so it should be removed
    // from the scheduled messages list before adding
    this.removeScheduledMessage(message);

    this.doMessage(this.state.messages, 'add', message);
  }

  addScheduledMessage(message) {
    // The messages won’t reorder based on the comparator
    // if we add (by merging) a message. Removing, then
    // adding will trigger the collection to place it
    // in the right spot
    this.removeScheduledMessage(message);

    this.doMessage(this.state.messagesScheduled, 'add', message);
  }

  removeMessage(message) {
    this.doMessage(this.state.messages, 'remove', message);
  }

  removeScheduledMessage(message) {
    this.doMessage(this.state.messagesScheduled, 'remove', message);
  }

  removeClientCreatedDuplicates(groupKey: string | number, newMessages: Messages) {
    const dupes = this.state.messages[groupKey].filter(m =>
      m.getIsClientCreated() === true &&
        newMessages.some(n => n.getBody() === m.getBody())
    );
    dupes.forEach((d) => this.state.messages[groupKey].remove(d));
  }

  getGap(groupKey) {
    return this.state.gaps[groupKey];
  }

  isDoneLoadingForGroup(groupKey) {
    return this.state.loadingState[groupKey] === LoadingStates.LOADED;
  }

  getMessages(groupKey) {
    return this.state.messages[groupKey] || new Messages();
  }

  getMessagesScheduled(groupKey) {
    if (groupKey in this.state.messagesScheduled) {
      this.state.messagesScheduled[groupKey]
        .filter(message => message.getSendAt() < new Date())
        .forEach(message => this.removeScheduledMessage(message));
    } else {
      this.state.messagesScheduled[groupKey] = new Messages();
    }
    return this.state.messagesScheduled[groupKey];
  }

  handleDispatch(action: Object): void {
    switch (action.type) {
      case ActionTypes.RESET_STORE:
        this.resetState();
        break;
      case ActionTypes.SEND_GROUP_MESSAGE:
        this.addMessage(action.message);
        break;
      case ActionTypes.SEND_GROUP_MESSAGE_ERROR:
        this.removeMessage(action.message);
        break;
      case ActionTypes.SEND_GROUP_SCHEDULED_MESSAGE:
        this.addScheduledMessage(action.message);
        break;
      case ActionTypes.SEND_GROUP_SCHEDULED_MESSAGE_ERROR:
        this.removeScheduledMessage(action.message);
        break;
      case ActionTypes.UPDATED_GROUP_SCHEDULED_MESSAGE:
        this.addScheduledMessage(action.message);
        break;
      case ActionTypes.DESTROY_GROUP_SCHEDULED_MESSAGE:
        this.removeScheduledMessage(action.message);
        break;
      case ActionTypes.LIST_GROUP_MESSAGES: {
        const { groupKey, messages } = action;
        if (!this.state.messages[groupKey]) {
          this.state.messages[groupKey] = new Messages();
        }
        this.state.loadingState[groupKey] = LoadingStates.LOADED;
        // assume this is the end until we see a gap
        this.state.gaps[groupKey] = undefined;
        this.removeClientCreatedDuplicates(groupKey, messages);
        messages.forEach(message => {
          const type = message.getType();
          if (type === 'message' || type === 'payment_request') {
            this.state.messages[groupKey].add(message);
          } else if (type === 'gap') {
            this.state.gaps[groupKey] = message;
          }
        });
        this.emitChange();
        break;
      }
      case ActionTypes.LIST_GROUP_SCHEDULED_MESSAGES:
        this.state.messagesScheduled[action.groupKey] = action.messages;
        this.emitChange();
        break;
      default:
        break;
    }
  }
}

export default new MessageStore();



// WEBPACK FOOTER //
// ./src/groups/stores/MessageStore.js